import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import { Input } from "@frui.ts/bootstrap";
import type { ViewComponent } from "@frui.ts/views";
import { preventDefault, registerView } from "@frui.ts/views";
import Button from "@edaz/edazkit/Button/index";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import Loader from "@edaz/edazkit/Loader/index";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay/index";
import Uploader from "@edaz/edazkit/Uploader";
import { observer } from "mobx-react-lite";
import React from "react";
import { Col, Form, Modal } from "react-bootstrap";
import localizationProvider from "localization/localizationProvider";
import { File } from "views/common/file";
import DistributorStornoViewModel from "viewModels/callCenter/actions/distributorStornoViewModel";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";

const DistributorStornoView: ViewComponent<DistributorStornoViewModel> = observer(({ vm }) => {
  let body: React.ReactFragment;
  let header: React.ReactNode = null;

  if (vm.uploading) {
    body = (
      <div className="text-center py-5">
        <div className="d-flex justify-content-center my-4">
          <Loader />
        </div>
        <span className="font-weight-bold text-uppercase">{vm.translate("uploading")}</span>
      </div>
    );
  } else if (vm.response) {
    body = (
      <div className="text-center py-4">
        <Icon className="text-primary mb-4" icon={IconType.CircleCheck} width={64} height={64} />
        <h4 className="mb-4">{vm.translate("complete")}</h4>
        <div className="mb-4">{vm.translate("description")}</div>
        <Button size="sm" className="text-uppercase" onClick={vm.onDownloadClick}>
          {vm.translate("downloadFile")}
        </Button>
      </div>
    );
  } else if (vm.error) {
    body = (
      <div className="text-center py-4">
        <Icon className="text-danger mb-4" icon={IconType.Cross} width={64} height={64} />
        <h4 className="mb-2">{vm.translate("error")}</h4>
        <div className="mb-4">{vm.error}</div>
        <Button size="sm" className="text-uppercase" onClick={vm.onResetClick}>
          {vm.translate("reset")}
        </Button>
      </div>
    );
  } else {
    header = <Modal.Title>{vm.name}</Modal.Title>;

    body = (
      <LoaderOverlay loading={vm.busyWatcher.isBusy}>
        <Form onSubmit={preventDefault(vm.onSubmit)}>
          <Form.Row>
            <FormGroup as={Col} target={vm.item} property="distributorId">
              <FormLabel>{vm.translateAttribute("distributorId")}</FormLabel>
              <AdvancedSelect mode="key" items={vm.distributorOptions} />
            </FormGroup>
          </Form.Row>
          {vm.item.file ? (
            <File file={vm.item.file} onRemoveClick={vm.onRemoveFileClick} error={vm.fileError} />
          ) : (
            <Uploader
              onDrop={vm.onDrop}
              localization={localizationProvider.uploaderLocalization}
              label=""
              accept={[".csv"]}
              error={vm.fileError}
            />
          )}
          <Form.Row>
            <FormGroup as={Col} target={vm} property="cancellationRecordId">
              <FormLabel>{vm.translateAttribute("cancellationRecordId")}</FormLabel>
              <Input errorMessage={vm.cancellationRecordIdError} />
            </FormGroup>
          </Form.Row>
          <Form.Row>
            <div className="col">
              <Button size="sm" className="text-uppercase" variant="light" block={true} onClick={vm.onClose}>
                {vm.translate("cancel")}
              </Button>
            </div>
            <div className="col">
              <Button size="sm" className="text-uppercase" variant="primary" block={true} type="submit">
                {vm.translate("storno")}
              </Button>
            </div>
          </Form.Row>
        </Form>
      </LoaderOverlay>
    );
  }

  return (
    <Modal show={true} onHide={vm.onClose} dialogClassName={vm.uploading || vm.response || vm.error ? "modal-sm" : ""} centered>
      <Modal.Header closeButton={!vm.uploading && !vm.response}>{header}</Modal.Header>
      <Modal.Body>{body}</Modal.Body>
    </Modal>
  );
});

registerView(DistributorStornoView, DistributorStornoViewModel);
